/**
 * Chip layer — port of
 *   packages/amgroulette_v2/lib/presentation/flame/roulette_flame_game.dart
 *
 * Roulette has 162 distinct bet areas (BetID 55..212). Each area has a
 * scatter rect (`ChipRandomPosition(minXpercent, rangeXpx, minYpercent, rangeYpx)`)
 * stored in CHIPS_POSITION below — transcribed 1:1 from the Flame map.
 *
 * Animations (mirrors roulette_flame_game.dart):
 *   - Placement: chip flies in from PLAYER_ORIGIN to the scattered target,
 *     300 ms linear.
 *   - Result:    chips on bet areas that cover the winning number fly to the
 *     player; everything else flies to the dealer. 500 ms with 20 ms × index
 *     stagger.
 *   - hardClear: instant wipe on Clear / new round.
 */
import { Container } from 'pixi.js';
import { ChipSprite, preloadCoinTextures } from './chipSprite';
import { Easings, tween } from './tween';
import { REF_HEIGHT, REF_WIDTH } from '../config';
import { BET_ID_INFO } from '../types/game';

interface XY {
  x: number;
  y: number;
}

/**
 * `ChipRandomPosition(minX, rangeXpx, minY, rangeYpx)` from
 * roulette_flame_game.dart line 17. minX/minY are PERCENTAGES of the design
 * resolution; range values are pixels.
 */
interface ScatterRect {
  minX: number;
  rangeXpx: number;
  minY: number;
  rangeYpx: number;
}

/**
 * 162 entries transcribed from chipsPosition (roulette_flame_game.dart:29-187).
 * Keyed by numeric betID.
 */
const CHIPS_POSITION: ReadonlyMap<number, ScatterRect> = new Map([
  [55, { minX: 0.362, rangeXpx: 10, minY: 0.34, rangeYpx: 100 }],
  [56, { minX: 0.397, rangeXpx: 8,  minY: 0.50, rangeYpx: 25 }],
  [57, { minX: 0.397, rangeXpx: 8,  minY: 0.39, rangeYpx: 25 }],
  [58, { minX: 0.397, rangeXpx: 8,  minY: 0.29, rangeYpx: 25 }],
  [59, { minX: 0.429, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [60, { minX: 0.429, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [61, { minX: 0.429, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [62, { minX: 0.462, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [63, { minX: 0.462, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [64, { minX: 0.464, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [65, { minX: 0.497, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [66, { minX: 0.497, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [67, { minX: 0.497, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [68, { minX: 0.532, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [69, { minX: 0.532, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [70, { minX: 0.532, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [71, { minX: 0.565, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [72, { minX: 0.565, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [73, { minX: 0.565, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [74, { minX: 0.600, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [75, { minX: 0.600, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [76, { minX: 0.600, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [77, { minX: 0.633, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [78, { minX: 0.633, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [79, { minX: 0.633, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [80, { minX: 0.664, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [81, { minX: 0.664, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [82, { minX: 0.664, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [83, { minX: 0.696, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [84, { minX: 0.696, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [85, { minX: 0.696, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [86, { minX: 0.731, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [87, { minX: 0.731, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [88, { minX: 0.731, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [89, { minX: 0.768, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [90, { minX: 0.764, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [91, { minX: 0.764, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [92,  { minX: 0.397, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [93,  { minX: 0.414, rangeXpx: 8, minY: 0.50, rangeYpx: 10 }],
  [94,  { minX: 0.397, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [95,  { minX: 0.414, rangeXpx: 8, minY: 0.41, rangeYpx: 10 }],
  [96,  { minX: 0.414, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
  [97,  { minX: 0.430, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [98,  { minX: 0.448, rangeXpx: 8, minY: 0.50, rangeYpx: 10 }],
  [99,  { minX: 0.430, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [100, { minX: 0.447, rangeXpx: 8, minY: 0.41, rangeYpx: 10 }],
  [101, { minX: 0.450, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
  [102, { minX: 0.465, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [103, { minX: 0.481, rangeXpx: 8, minY: 0.51, rangeYpx: 10 }],
  [104, { minX: 0.465, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [105, { minX: 0.481, rangeXpx: 8, minY: 0.41, rangeYpx: 10 }],
  [106, { minX: 0.482, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
  [107, { minX: 0.496, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [108, { minX: 0.514, rangeXpx: 8, minY: 0.51, rangeYpx: 10 }],
  [109, { minX: 0.499, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [110, { minX: 0.515, rangeXpx: 8, minY: 0.41, rangeYpx: 10 }],
  [111, { minX: 0.515, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
  [112, { minX: 0.531, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [113, { minX: 0.548, rangeXpx: 8, minY: 0.51, rangeYpx: 10 }],
  [114, { minX: 0.532, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [115, { minX: 0.548, rangeXpx: 8, minY: 0.41, rangeYpx: 10 }],
  [116, { minX: 0.547, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
  [117, { minX: 0.565, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [118, { minX: 0.582, rangeXpx: 8, minY: 0.51, rangeYpx: 10 }],
  [119, { minX: 0.564, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [120, { minX: 0.584, rangeXpx: 8, minY: 0.41, rangeYpx: 10 }],
  [121, { minX: 0.584, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
  [122, { minX: 0.598, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [123, { minX: 0.617, rangeXpx: 8, minY: 0.51, rangeYpx: 10 }],
  [124, { minX: 0.597, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [125, { minX: 0.617, rangeXpx: 8, minY: 0.41, rangeYpx: 10 }],
  [126, { minX: 0.617, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
  [127, { minX: 0.633, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [128, { minX: 0.649, rangeXpx: 8, minY: 0.51, rangeYpx: 10 }],
  [129, { minX: 0.633, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [130, { minX: 0.650, rangeXpx: 8, minY: 0.41, rangeYpx: 10 }],
  [131, { minX: 0.650, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
  [132, { minX: 0.664, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [133, { minX: 0.682, rangeXpx: 8, minY: 0.51, rangeYpx: 10 }],
  [134, { minX: 0.665, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [135, { minX: 0.684, rangeXpx: 8, minY: 0.41, rangeYpx: 10 }],
  [136, { minX: 0.684, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
  [137, { minX: 0.698, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [138, { minX: 0.718, rangeXpx: 10, minY: 0.50, rangeYpx: 10 }],
  [139, { minX: 0.700, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [140, { minX: 0.718, rangeXpx: 10, minY: 0.40, rangeYpx: 10 }],
  [141, { minX: 0.717, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
  [142, { minX: 0.732, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [143, { minX: 0.750, rangeXpx: 10, minY: 0.50, rangeYpx: 10 }],
  [144, { minX: 0.732, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [145, { minX: 0.749, rangeXpx: 10, minY: 0.40, rangeYpx: 10 }],
  [146, { minX: 0.751, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
  [147, { minX: 0.765, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [148, { minX: 0.766, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [149, { minX: 0.382, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [150, { minX: 0.379, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [151, { minX: 0.397, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [152, { minX: 0.430, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [153, { minX: 0.465, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [154, { minX: 0.500, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [155, { minX: 0.530, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [156, { minX: 0.566, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [157, { minX: 0.599, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [158, { minX: 0.633, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [159, { minX: 0.667, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [160, { minX: 0.697, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [161, { minX: 0.731, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [162, { minX: 0.767, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [163, { minX: 0.380, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [164, { minX: 0.413, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [165, { minX: 0.413, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [166, { minX: 0.450, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [167, { minX: 0.448, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [168, { minX: 0.482, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [169, { minX: 0.480, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [170, { minX: 0.518, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [171, { minX: 0.515, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [172, { minX: 0.547, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [173, { minX: 0.546, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [174, { minX: 0.581, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [175, { minX: 0.584, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [176, { minX: 0.614, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [177, { minX: 0.615, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [178, { minX: 0.650, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [179, { minX: 0.648, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [180, { minX: 0.681, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [181, { minX: 0.682, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [182, { minX: 0.715, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [183, { minX: 0.717, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [184, { minX: 0.748, rangeXpx: 8, minY: 0.46, rangeYpx: 10 }],
  [185, { minX: 0.748, rangeXpx: 8, minY: 0.35, rangeYpx: 10 }],
  [186, { minX: 0.414, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [187, { minX: 0.448, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [188, { minX: 0.482, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [189, { minX: 0.515, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [190, { minX: 0.548, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [191, { minX: 0.585, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [192, { minX: 0.615, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [193, { minX: 0.648, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [194, { minX: 0.682, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [195, { minX: 0.715, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [196, { minX: 0.748, rangeXpx: 8, minY: 0.56, rangeYpx: 10 }],
  [198, { minX: 0.798, rangeXpx: 10, minY: 0.50, rangeYpx: 25 }],
  [199, { minX: 0.798, rangeXpx: 10, minY: 0.39, rangeYpx: 25 }],
  [200, { minX: 0.798, rangeXpx: 10, minY: 0.29, rangeYpx: 25 }],
  [201, { minX: 0.420, rangeXpx: 100, minY: 0.60, rangeYpx: 50 }],
  [202, { minX: 0.565, rangeXpx: 100, minY: 0.60, rangeYpx: 50 }],
  [203, { minX: 0.695, rangeXpx: 100, minY: 0.60, rangeYpx: 50 }],
  [204, { minX: 0.414, rangeXpx: 30, minY: 0.70, rangeYpx: 50 }],
  [205, { minX: 0.748, rangeXpx: 30, minY: 0.70, rangeYpx: 50 }],
  [206, { minX: 0.548, rangeXpx: 30, minY: 0.70, rangeYpx: 50 }],
  [207, { minX: 0.615, rangeXpx: 30, minY: 0.70, rangeYpx: 50 }],
  [208, { minX: 0.682, rangeXpx: 30, minY: 0.70, rangeYpx: 50 }],
  [209, { minX: 0.482, rangeXpx: 30, minY: 0.70, rangeYpx: 50 }],
  [210, { minX: 0.382, rangeXpx: 8, minY: 0.51, rangeYpx: 10 }],
  [211, { minX: 0.382, rangeXpx: 8, minY: 0.40, rangeYpx: 10 }],
  [212, { minX: 0.382, rangeXpx: 8, minY: 0.30, rangeYpx: 10 }],
]);

const PLAYER_ORIGIN: XY = { x: REF_WIDTH / 2, y: REF_HEIGHT + 50 };
const DEALER_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 0.2 };
const PLAYER_WIN_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 1.1 };
const CENTER_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 0.5 };

interface ActiveChip {
  betId: number;
  sprite: ChipSprite;
}

export class ChipLayer {
  private readonly root: Container;
  private active: ActiveChip[] = [];

  constructor(parent: Container) {
    this.root = new Container();
    this.root.label = 'ChipLayer';
    parent.addChild(this.root);
    void preloadCoinTextures();
  }

  /**
   * Add a chip to a bet area. `chipIndex` is the picker index (0..N-1) —
   * determines coin colour. Mirrors roulette_flame_game.dart `assignNewChip`.
   */
  placeChip(betId: number, chipValue: number, chipIndex: number): void {
    const rect = CHIPS_POSITION.get(betId);
    if (!rect) return;

    const target: XY = {
      x: rect.minX * REF_WIDTH + Math.random() * rect.rangeXpx,
      y: rect.minY * REF_HEIGHT + Math.random() * rect.rangeYpx,
    };

    const sprite = new ChipSprite(chipValue, chipIndex);
    sprite.position.set(PLAYER_ORIGIN.x, PLAYER_ORIGIN.y);
    this.root.addChild(sprite);

    const from = { ...PLAYER_ORIGIN };
    tween({
      durationMs: 300,
      easing: Easings.linear,
      onUpdate: (t) => {
        sprite.position.set(
          from.x + (target.x - from.x) * t,
          from.y + (target.y - from.y) * t,
        );
      },
    });

    this.active.push({ betId, sprite });
  }

  /**
   * Animate all chips toward the wheel centre. Used after the wheel stops
   * but before the final settlement, so the player sees the chips converge
   * for the announcement. Matches `moveAllToCenter` in the Flutter Flame game.
   */
  animateAllToCenter(): void {
    this.active.forEach((c, i) => this.flyAndFade(c, CENTER_TARGET, i, 600, 0.5));
    // Don't destroy; settle() will handle that.
  }

  /**
   * Settle chips after the wheel result is known. Chips on bet areas whose
   * `numbers` cover `winningNumber` fly to the player; everything else flies
   * to the dealer.
   */
  settle(winningNumber: number): void {
    const oldActive = this.active;
    this.active = [];
    oldActive.forEach((c, i) => {
      const info = BET_ID_INFO.get(c.betId);
      const isWin = info != null && info.numbers.includes(winningNumber);
      const target = isWin ? PLAYER_WIN_TARGET : DEALER_TARGET;
      this.flyAndFade(c, target, i, 500, 0.2);
    });
    this.scheduleDestroyAll(oldActive, 800);
  }

  /** Wipe all chips immediately. Used on Clear / new round. */
  hardClear(): void {
    for (const c of this.active) c.sprite.destroy();
    this.active = [];
  }

  hasAnyChips(): boolean {
    return this.active.length > 0;
  }

  // -----------------------------------------------------------------

  private flyAndFade(
    c: ActiveChip,
    target: XY,
    index: number,
    durationMs: number,
    targetAlpha: number,
  ): void {
    const fromX = c.sprite.position.x;
    const fromY = c.sprite.position.y;
    const fromAlpha = c.sprite.alpha;
    const delayMs = 20 * index;

    window.setTimeout(() => {
      tween({
        durationMs,
        easing: Easings.linear,
        onUpdate: (t) => {
          c.sprite.position.set(
            fromX + (target.x - fromX) * t,
            fromY + (target.y - fromY) * t,
          );
          c.sprite.alpha = fromAlpha + (targetAlpha - fromAlpha) * t;
        },
      });
    }, delayMs);
  }

  private scheduleDestroyAll(list: ActiveChip[], delayMs: number): void {
    window.setTimeout(() => {
      for (const c of list) {
        try {
          c.sprite.destroy();
        } catch {
          /* already destroyed */
        }
      }
    }, delayMs);
  }
}
